<template>
    <div class="cityStateCountryPage">
        <div v-if="loading" class="q-my-xl text-center">
            <q-spinner color="red-8" size="4rem" />
        </div>

        <template v-if="generalInfo">
            <HomeSlider :countrySlideImgProp="countrySlideImg" />
            <homeFeatures />

            <q-separator size="2px" />

            <div class="q-px-container exploreDealsSpacing">
                <HomePropertyLists
                    sectionNameProp="bestDeals"
                    :itemsToShowProp="$q.screen.gt.sm ? 4 : ($q.screen.lt.sm ? 1.20 : 2.20)"
                    :isHomeCityStateCountry="true"
                    :stateProp="state"
                    :countryProp="country"
                    :cityStateCountryProp="searchLocation"
                    :countryLatLngProp="latLong" />
                <FindLastMinuteDeals :itemsToShowProp="$q.screen.gt.sm ? 3 : ($q.screen.lt.sm ? 1.20 : 2.20)" :countryLatLngProp="latLong" :cityStateCountryProp="searchLocation" />

                <HomeToGoPropertyList
                    :isHomeCityStateCountry="true"
                    :countryLatLngProp="latLong"
                    :cityStateCountryProp="searchLocation" />

                <template v-if="filteredAmenities.length">
                    <div class="headingToday">
                        <h2 class="exploreDeals">Favorite Vacation Rental Amenities in <span class="text-capitalize">{{ regionName }}</span></h2>
                    </div>
                    <div class="destinationPlaces row wrap">
                        <template v-if="$q.screen.gt.xs">
                            <div v-for="(place, i) in filteredAmenities" :key="i" class="favPlaceStyle col-6 col-md-3">
                                <a class="placeLink" :href="formCompleteURL(place.link)" target="_blank" rel="noopener noreferrer nofollow">
                                    <q-img class="favouriteImg" :src="place.image" :alt="place.title">
                                        <div class="imageText absolute-full flex flex-center">{{ place.title }}</div>
                                    </q-img>
                                    <div class="placeTitle">{{ favouriteDesciption(place) }}</div>
                                </a>
                            </div>
                        </template>

                        <template v-else>
                            <Carousel v-bind="settings">
                                <Slide v-for="(place, i) in filteredAmenities" :key="i">
                                    <div class="placeLink" @click="openLink(place.link)">
                                        <q-img class="favouriteImg" :src="place.image" :alt="place.title">
                                            <div class="imageText absolute-full flex flex-center">{{ place.title }}</div>
                                        </q-img>
                                        <div class="placeTitle">{{ favouriteDesciption(place) }}</div>
                                    </div>
                                </Slide>
                            </Carousel>
                        </template>
                    </div>
                </template>

                <template v-if="filteredPropertyTypes.length">
                    <div class="headingToday">
                        <h2 class="exploreDeals">Last Minute Houses, Cabins, Apartments, Condos & Cottages</h2>
                    </div>
                    <div class="destinationPlaces row wrap">
                        <template v-if="$q.screen.gt.xs">
                            <div v-for="(place, i) in filteredPropertyTypes" :key="i" class="favPlaceStyle col-6 col-md-3">
                                <a class="placeLink" :href="formCompleteURL(place.link)" target="_blank" rel="noopener noreferrer nofollow">
                                    <q-img class="favouriteImg" :src="place.image" :alt="place.title">
                                        <div class="imageText absolute-full flex flex-center">{{ place.title }}</div>
                                    </q-img>
                                    <div class="placeTitle">{{ place.count }} Last Minute {{ place.title }}</div>
                                </a>
                            </div>
                        </template>

                        <template v-else>
                            <Carousel v-bind="settings">
                                <Slide v-for="(place, i) in filteredPropertyTypes" :key="i">
                                    <div class="placeLink" @click="openLink(place.link)">
                                        <q-img class="favouriteImg" :src="place.image" :alt="place.title">
                                            <div class="imageText absolute-full flex flex-center">{{ place.title }}</div>
                                        </q-img>
                                        <div class="placeTitle">{{ favouriteDesciption(place) }}</div>
                                    </div>
                                </Slide>
                            </Carousel>
                        </template>
                    </div>
                </template>

                <template v-if="generalInfo.description">
                    <div class="headingToday">
                        <h2 class="exploreDeals">
                            <span class="text-capitalize">{{ sectionName }}</span> Description
                        </h2>
                    </div>
                    <div v-html="generalInfo.description" />
                </template>

                <template v-if="generalInfo.things_todo">
                    <div class="headingToday">
                        <h2 class="exploreDeals">Things to Do in <span class="text-capitalize">{{ regionName }}</span></h2>
                    </div>
                    <div v-html="generalInfo.things_todo" />
                </template>

                <template v-if="generalInfo.shopping">
                    <div class="headingToday">
                        <h2 class="exploreDeals">Shopping in <span class="text-capitalize">{{ regionName }}</span></h2>
                    </div>
                    <div v-html="generalInfo.shopping" />
                </template>

                <template v-if="generalInfo.dining">
                    <div class="headingToday">
                        <h2 class="exploreDeals">Dining in <span class="text-capitalize">{{ regionName }}</span></h2>
                    </div>
                    <div v-html="generalInfo.dining" />
                </template>

                <div v-if="moreLastMinuteData.length" class="headingToday">
                    <h2 class="exploreDeals">
                        More Last Minute Vacation Rentals in <span class="text-capitalize">{{ generalInfo.state ?? generalInfo.country }}</span>
                    </h2>
                </div>

                <TransitionGroup name="list" class="colCount" tag="div">
                    <div class="ellipsis" v-for="(item, i) in moreLastMinuteData" :key="i">
                        <a v-if="item.is_added" :href="formCompleteURL(item.state_url || item.city_url)" class="moreRentalLink" target="_blank" rel="noopener noreferrer">
                            {{ item.display_name }} Last Minute Rentals
                        </a>
                        <a v-else :href="generateSearchLink(item.display_name, item.latitude, item.longitude)" class="moreRentalLink" target="_blank" rel="noopener noreferrer nofollow">
                            {{ item.display_name }} Last Minute Rentals
                        </a>
                    </div>
                </TransitionGroup>
            </div>
        </template>

        <template v-if="searchLocation && !generalInfo">
            <SearchPage :regionLocationProp="searchLocation" :setCanonicalURL="true" />
        </template>
    </div>
</template>

<script>
import _ from 'lodash';
import { ref } from 'vue';
import { useMeta } from 'quasar';
import { Carousel, Slide } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import HomeSlider from '../home/HomeSlider.vue';
import homeFeatures from './homeFeatures.vue';
import FindLastMinuteDeals from '../home/FindLastMinuteDeals.vue';
import SearchPage from './search.vue';
import HomePropertyLists from '../home/HomePropertyList.vue';
import HomeToGoPropertyList from '../home/HomeToGoPropertyList.vue';

export default {
    setup() {
        const metaTitle = ref(null);
        const metaDescription = ref(null);
        const pageURL = window.location.origin + window.location.pathname;

        useMeta(() => ({
            title: metaTitle.value,
            meta: {
                title: { name: 'title', content: metaTitle.value },
                description: { name: 'description', content: metaDescription.value },
                ogTitle: { property: 'og:title', content: metaTitle.value },
                ogDescription: { property: 'og:description', content: metaDescription.value },
                ogURL: { property: 'og:url', content: pageURL },

                /* Twitter meta tags */
                twitterCard: { property: 'twitter:card', content: 'summary' },
                twitterSite: { property: 'twitter:site', content: '@getawaygogo' },
                twitterTitle: { property: 'twitter:title', content: metaTitle.value },
                twitterDescription: { property: 'twitter:description', content: metaDescription.value },
                twitterURL: { property: 'twitter:url', content: pageURL },
            },
            script: {
                ldJson: {
                    type: 'application/ld+json',
                    innerHTML: `{ "@context": "http://www.schema.org", "@type": "TravelAgency", "name": "getawayGogo", "description": "${metaDescription.value}", "address": { "@type": "PostalAddress", "addressLocality": "San Clemente", "addressRegion": "California", "addressCountry": "United States", "postalCode": "92672" }}`,
                },
            },
            link: { canonical: { rel: 'canonical', href: pageURL } },
        }));

        function setMetaTitle(title, description) {
            metaTitle.value = title;
            metaDescription.value = description;
        }

        return {
            setMetaTitle,
        };
    },
    components: {
        HomeSlider,
        homeFeatures,
        FindLastMinuteDeals,
        SearchPage,
        Carousel,
        Slide,
        HomePropertyLists,
        HomeToGoPropertyList,
    },
    data() {
        return {
            loading: false,
            amenitiesData: null,
            moreLastMinuteData: [],
            generalInfo: null,
            propertyTypeData: null,
            countrySlideImg: [],
            regionName: null,
            latLong: null,
            searchLocation: null,
            settings: {
                itemsToShow: 2.2,
                snapAlign: 'start',
            },
            propertyTypes: 0,
            amenities: 0,
            country: null,
            state: null,
        };
    },
    provide() {
        return {
            sliderTitleText: () => this.regionName,
        };
    },
    computed: {
        sectionName() {
            if (this.$route.name === 'countryPage') return 'country';
            if (this.$route.name === 'statePage') return 'state';
            return 'city';
        },
        filteredAmenities() {
            if (this.amenitiesData && this.amenitiesData.length) {
                return this.amenitiesData.filter((amenity) => amenity.count > 0);
            }
            return [];
        },
        filteredPropertyTypes() {
            if (this.propertyTypeData && this.propertyTypeData.length) {
                return this.propertyTypeData.filter((type) => type.count > 0);
            }
            return [];
        },
    },
    methods: {
        openLink(link) {
            const fullLink = this.formCompleteURL(link);
            window.open(fullLink, '_blank');
        },
        getPayload() {
            return {
                params: {
                    link: decodeURI(this.$route.path),
                    section: this.sectionName,
                },
            };
        },
        getamenitiesData() {
            this.loading = true;
            const payload = this.getPayload();
            this.axios.get('/api/favorite-places-amenities', payload).then((response) => {
                if (response.data.data && Object.keys(response.data.data).length) {
                    this.amenitiesData = response.data.data;
                }
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
        getPropertyTypeData() {
            this.loading = true;
            const payload = this.getPayload();
            this.axios.get('/api/favorite-places-property-types', payload).then((response) => {
                if (response.data.data && Object.keys(response.data.data).length) {
                    this.propertyTypeData = response.data.data;
                }
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
        getMoreLastMinuteVacationRentals() {
            const payload = this.getPayload();
            this.axios.get('/api/more-last-minute-vacation-rentals', payload).then((response) => {
                const responeData = response.data.data;
                if (responeData && responeData?.length) {
                    this.moreLastMinuteData = responeData;
                }
            });
        },
        getGeneralInfo() {
            this.loading = true;
            const payload = {
                url: decodeURI(this.$route.path),
                section_name: this.sectionName,
            };
            this.axios.post('/api/top_last_minute_destinations_details', payload).then((response) => {
                const responseData = response.data.data;

                if (responseData && Object.keys(responseData).length) {
                    this.generalInfo = responseData;
                    this.latLong = { lat: _.get(this.generalInfo, 'latitude'), lng: _.get(this.generalInfo, 'longitude') };
                    this.setMetaTitle(this.generalInfo.meta_title, this.generalInfo.meta_description);

                    if (this.sectionName === 'country') {
                        this.regionName = this.generalInfo.country;
                        this.country = this.generalInfo.country;
                        this.searchLocation = this.regionName;
                        this.setSliderImages(this.generalInfo?.home_country_photos || false);
                    } else if (this.sectionName === 'state') {
                        this.regionName = this.generalInfo.state;
                        this.searchLocation = `${this.regionName}, ${this.generalInfo.country}`;
                        this.country = this.generalInfo.country;
                        this.state = this.regionName;
                        this.setSliderImages(this.generalInfo?.home_state_photos || false);
                    } else {
                        this.regionName = this.generalInfo.cities_state_country;
                        this.searchLocation = `${this.regionName}, ${this.generalInfo.state}, ${this.generalInfo.country}`;
                        this.country = this.generalInfo.country;
                        this.state = this.generalInfo.state;
                        this.setSliderImages(this.generalInfo?.home_cities_photos || false);
                    }
                } else {
                    this.searchLocation = response.data.message;
                }
            }).then(() => {
                this.loading = false;
            });
        },
        setSliderImages(imageList) {
            if (this.$q.screen.lt.sm && imageList && imageList.length > 1) {
                imageList.length = 1;
            }
            if (imageList) {
                imageList.forEach((img) => {
                    this.countrySlideImg.push(img.photo_url);
                });
            }
        },
        favouriteDesciption(place) {
            let description = `${place.count} ${place.title}`;
            description += `${place.count}` === '1' ? ' Rental Deal' : ' Rental Deals';
            return description;
        },
        generateSearchLink(location, lat, lng) {
            return this.formCompleteURL(`/s?location=${location}&distance=500&radiusUnit=mi&lat=${lat}&lng=${lng}`);
        },
    },
    created() {
        this.getGeneralInfo();
        this.getamenitiesData();
        this.getPropertyTypeData();
        this.getMoreLastMinuteVacationRentals();
    },
};
</script>

<style lang="scss" scoped>
.exploreDealsSpacing {
    margin-bottom: 2rem;
}

.colCount {
    column-count: 3;
}

.list-enter-active,
.list-leave-active {
    transition: all 1.5s ease;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
}

.moreRentalLink {
    text-decoration: none;
    font-size: 1.1rem;
    color: #484848;

    &:hover {
        color: #888;
    }
}

.destinationPlaces {
    display: flex;
    flex-wrap: wrap;
    row-gap: 2rem;
    column-gap: 2rem;

    .placeLink {
        color: #000;
        text-decoration: unset;
    }

    .favPlaceStyle {
        width: calc(25% - 1.5rem);
    }

    .favouriteImg {
        height: 30rem;
        border-radius: 1rem;
    }

    .imageText {
        background: unset;
        text-align: center;
        font-size: 2.125rem;
        font-weight: 700;

        &:hover {
            background: rgba(0, 0, 0, 0.47);
        }
    }

    .placeTitle {
        padding-top: 1rem;
        font-weight: 600;
    }
}

@media screen and (min-width:1024px) {
    .exploreDealsSpacing {
        margin-top: -1.5rem;
    }
}

@media screen and (min-width:$breakpoint-md) {
    .exploreDealsSpacing {
        margin-top: -2rem;
    }
}

@media only screen and (max-width: 1599px) {
    .destinationPlaces {
        .favouriteImg {
            height: 23.5rem;
        }

        .imageText {
            font-size: 1.6rem;
        }
    }
}

@media only screen and (max-width: 1199px) {
    .exploreDealsSpacing {
        margin-bottom: 2.25rem;
    }

    .destinationPlaces .favouriteImg {
        height: 21.5rem;
    }
}

@media only screen and (max-width:$breakpoint-sm) {

    .exploreDealsSpacing {
        margin-bottom: 2rem;
    }

    .colCount {
        column-count: 2;
    }

    .destinationPlaces {

        .favouriteImg {
            height: 30rem;
        }

        .favPlaceStyle {
            width: calc(50% - 1rem);
        }

    }
}

@media only screen and (max-width:$breakpoint-xs) {
    .colCount {
        column-count: 1;
    }

    .destinationPlaces {
        .carousel {
            width: 100%;
        }

        .placeLink {
            cursor: pointer;
            width: 100%;
            max-width: 92%;
            user-select: none;
        }

        .favouriteImg {
            height: 17rem;
        }

        .placeTitle {
            text-align: left;
        }
    }
}

@media only screen and (max-width:479px) {
    .destinationPlaces {
        .favouriteImg {
            height: 16rem;
        }

        .imageText {
            padding: 1rem 1.6rem;
            font-size: 1.3rem;
        }
    }
}

@media only screen and (max-width:375px) {
    .destinationPlaces .favouriteImg {
        height: 14rem;
    }
}
</style>
